// QC defect taxonomy registry (mig 178). One row per defect type, keyed by
// a stable `code` (e.g. 'BLACK_SPOT'). Templates pick which subset of the
// registry to expose on each MULTI_SELECT item; responses store codes;
// the UI looks up labels at render time so renaming a defect propagates
// everywhere.

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'
import { useCommodities } from '@/hooks/useCommodities'

export interface QcDefectType {
  defect_id:     string
  code:          string
  label:         string
  category:      string | null
  description:   string | null
  is_active:     boolean
  display_order: number
  tenant_id:     string | null   // NULL = global baseline; set = tenant-owned (mig 20260629160000)
}

const STALE = 5 * 60_000

// A commodity id that matches no row. Used when a commodity NAME can't be
// resolved to an id, so the query returns nothing instead of falling through to
// the NULL/default set — which would leak citrus defects onto a berry form.
const UNMATCHABLE_ID = '00000000-0000-0000-0000-000000000000'

// Which commodity_ids the defect taxonomy is scoped to, or the NULL/default set.
export type DefectScope = { ids: string[] } | { nullSet: true }

// Pure precedence — split out of the hook so the resolution order is one small,
// obvious unit (same shape as resolveManifest in useCommodityTemplates). Every
// branch here is load-bearing; see the hook's comment for why.
export function resolveDefectScope(args: {
  overrideName:     string | null
  flowCommodityName: string | null
  commodities:      { commodity_id: string; name: string }[]
  enabledIds:       string[]
}): DefectScope {
  const { overrideName, flowCommodityName, commodities, enabledIds } = args
  const idOf = (name: string) =>
    commodities.find(c => c.name === name)?.commodity_id ?? UNMATCHABLE_ID
  // 1. Explicit caller override (e.g. the active run's commodity).
  if (overrideName) return { ids: [idOf(overrideName)] }
  // 2. Citrus → the NULL/default set. Unchanged, and load-bearing.
  if (flowCommodityName === null) return { nullSet: true }
  // 3. What the tenant PACKS, intersected with commodities that still exist
  //    (an enabled row pointing at a deleted commodity must not scope to it).
  //    SORTED because this array lands in a react-query key: tenant_commodities
  //    is read without an ORDER BY, and Postgres makes no order guarantee, so an
  //    unsorted list would hash to a different key each time the rows came back
  //    in a different order — a second cache entry for identical data, and a
  //    redundant refetch. Sort here rather than in the query so the guarantee
  //    holds whatever the caller passes.
  const known = new Set(commodities.map(c => c.commodity_id))
  const packed = enabledIds.filter(id => known.has(id)).sort()
  if (packed.length > 0) return { ids: packed }
  // 4. Nothing enabled / offline-cold → the flow's commodity (prior behaviour).
  return { ids: [idOf(flowCommodityName)] }
}

export function useQcDefectTypes(opts: { onlyActive?: boolean; commodityName?: string | null } = {}) {
  const onlyActive = opts.onlyActive ?? false
  // Scope the taxonomy to the commodities this packhouse actually PACKS
  // (tenant_commodities, mig 1041) — NOT the flow's single commodity.
  //
  // production_flow is one scalar per tenant; what a packhouse packs is a SET,
  // and the two have already diverged in prod: Anchor runs the BLUEBERRY flow
  // but packs only STRAWBERRIES, so it saw only BB_ defects for strawberry
  // fruit. Resolution order:
  //
  //   1. opts.commodityName — an explicit caller override (e.g. the active
  //      RUN's commodity). Still wins.
  //   2. CITRUS (manifest.commodityName === null) → the commodity_id IS NULL
  //      default set. LOAD-BEARING, do not "simplify" away: citrus defects live
  //      in the NULL set by historical accident, and a citrus tenant's enabled
  //      commodities (ORANGES / SOFT CITRUS / LEMONS) have NO tagged defects —
  //      scoping citrus by enabled commodities returns ZERO rows.
  //   3. The tenant's enabled commodities → the union of their tagged defects.
  //   4. Enabled set empty / offline-cold → fall back to the flow's commodity
  //      (previous behaviour), so an un-configured tenant never goes blank.
  //
  // useEnabledCommodities already returns the FULL catalog when the enabled set
  // is empty/cold, so step 3 must distinguish "genuinely enabled" from that
  // fallback — hence the raw tenant-commodity id read below rather than reusing
  // that hook's filtered list.
  const flowCommodityName = useFlowManifest().commodityName
  const overrideName = opts.commodityName || null
  const { data: commodities = [] } = useCommodities()
  const { data: enabledIds = [] } = useTenantCommodityIds()

  const scope = useMemo(
    () => resolveDefectScope({ overrideName, flowCommodityName, commodities, enabledIds }),
    [overrideName, flowCommodityName, commodities, enabledIds],
  )

  return useQuery({
    // The resolved scope MUST be in the key — otherwise one tenant's taxonomy
    // is served to the next from cache.
    queryKey: ['qc-defect-types', onlyActive, scope],
    staleTime: STALE,
    // Don't fetch on a half-resolved scope: before the commodity catalog loads,
    // every name resolves to UNMATCHABLE_ID and we'd cache an empty taxonomy.
    enabled: 'nullSet' in scope || commodities.length > 0,
    queryFn: async () => {
      let q = supabase.from('qc_defect_types')
        .select('*')
        .order('display_order', { ascending: true })
        .order('label',         { ascending: true })
      if (onlyActive) q = q.eq('is_active', true)
      if ('nullSet' in scope) {
        q = q.is('commodity_id', null)
      } else {
        // Unknown commodity → match nothing rather than leaking the default
        // set. UNMATCHABLE_ID keeps that guard: an unresolvable name yields an
        // id that matches no row, never a fall-through to the citrus defects.
        q = q.in('commodity_id', scope.ids)
      }
      const { data, error } = await q
      if (error) throw error
      return data as QcDefectType[]
    },
  })
}

// The active tenant's enabled commodity IDs, RAW — no empty-set fallback.
// useEnabledCommodities() deliberately returns the whole catalog when the set is
// empty; for scoping a taxonomy we need to KNOW it was empty so we can fall back
// to the flow's commodity instead of showing every commodity's defects.
function useTenantCommodityIds() {
  return useQuery({
    queryKey: ['tenant-commodity-ids'],
    staleTime: STALE,
    queryFn: async () => {
      // RLS scopes this to the active tenant.
      const { data, error } = await supabase
        .from('tenant_commodities')
        .select('commodity_id')
      if (error) throw error
      return (data ?? []).map(r => r.commodity_id as string)
    },
  })
}

// Helper that returns a Map<code, label> for fast lookup at render time.
// Returns the code itself when a row hasn't been loaded yet or has been
// removed — so the UI never shows a blank box for a stale code.
//
// Deliberately NOT commodity-scoped. This resolves codes that are ALREADY
// STORED (on responses and on template items), and a stored code is a fact —
// whatever taxonomy it came from. Codes are unique per (tenant_id, code)
// (mig 20260630042727), so there is nothing to disambiguate.
//
// It used to inherit useQcDefectTypes' commodity filter, so on a tenant that
// packs more than one commodity a stored code from the "other" one rendered as
// the raw code ('SB_MOULD' instead of 'Grey mould (Botrytis)') — and the
// `?? code` fallback below made that look intentional rather than broken.
export function useDefectLabelMap(): { labelOf: (code: string) => string; loading: boolean } {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['qc-defect-labels'],
    staleTime: STALE,
    queryFn: async () => {
      // RLS already scopes to the tenant's own rows + the platform baseline.
      const { data, error } = await supabase
        .from('qc_defect_types')
        .select('code, label')
      if (error) throw error
      return (data ?? []) as { code: string; label: string }[]
    },
  })
  const map = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of rows) m.set(r.code, r.label)
    return m
  }, [rows])
  return {
    labelOf: (code: string) => map.get(code) ?? code,
    loading: isLoading,
  }
}
